/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import logo from "../../assets/images/contact-black-icon.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";

const Sidebar = forwardRef(({ isSidebarCollapsed, chatHistory = [], resetChat, setChatHistory }, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [apiHistory, setApiHistory] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const baseapiUrl = process.env.REACT_APP_API_URL || "";
  const [selectedThread, setSelectedThread] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [editPopup, setEditPopup] = useState({ open: false, thread: null });
  const menuRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null); // close menu
      }
    };

    // Attach listener
    document.addEventListener("click", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);


  const handleNewChatClick = (e) => {
    setSelectedThread(null);
    setUser((prevUser) => ({
      ...prevUser,
      threadId: 0,    // set thread_id 0 for new chat
    }));
    e.preventDefault();
    if (location.pathname === "/new-chat") {
      resetChat?.();
    } else {
      navigate("/new-chat");
    }
  };

  const handlerouteClick = (e) => {
    setSelectedThread(null);
  };

  const openEditPopup = (thread) => {
    setEditPopup({ open: true, thread });
    setOpenMenu(null);
  };

  const handleReload = (thread_id) => {
    setOpenMenu(null);
    handleHistoryClick(thread_id);
  };

  const handleRename = async () => {
    setLoading(true);
    const newTitle = editPopup.thread.title;

    const url = `${baseapiUrl.replace(/\/+$/, "")}/renamethread/`;
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userid: user?.user_id,               
        thread_id: editPopup.thread.id,
        new_title: newTitle,
      }),
    });

    setEditPopup({ open: false, thread: null });

    fetchChatHistory(1); // reload list
    setLoading(false);
  };

  const handleDelete = async (thread_id) => {
    setOpenMenu(null);
    if (!window.confirm("Are you sure you want to delete this thread?")) return;
    setLoading(true);
    const url = `${baseapiUrl.replace(/\/+$/, "")}/deletethread/`;
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userid: user?.user_id,                
        thread_id,
      }),
    });

    fetchChatHistory(1); // reload
    setLoading(false);
  };


  // Fetch Chat History (getthread)
  const fetchChatHistory = async (page = 1) => {
    if (!user?.user_id) return;
    setLoading(true);
    setSelectedThread(null);

    try {
      const url = `${baseapiUrl.replace(/\/+$/, "")}/getthreads/`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userid: user.user_id,          
          page_no: page,
          page_size: pageSize,
        }),
      });

      const data = await response.json();

      if (Array.isArray(data.data?.threads) && data.data.threads.length > 0) {
        setApiHistory((prev) => (page === 1 ? data.data.threads : [...prev, ...data.data.threads]));
        setHasMore(data.data.threads.length === pageSize);
      } else {
        setHasMore(false);
      }


    } catch (error) {
      console.error("Error loading chat history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatHistory(1);
  }, [user?.user_id]);

  useImperativeHandle(ref, () => ({
    reloadHistory: () => fetchChatHistory(1),
  }));

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      const nextPage = pageNo + 1;
      setPageNo(nextPage);
      fetchChatHistory(nextPage);
    }
  };

  const handleHistoryClick = async (thread_id) => {
    setLoading(true);
    setSelectedThread(thread_id);

    // Store current thread_id globally
    setUser((prevUser) => ({
      ...prevUser,
      threadId: thread_id,
    }));

    try {
      const url = `${baseapiUrl.replace(/\/+$/, "")}/chat_history/`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userid: user?.user_id,                            
          thread_id: thread_id,
        }),
      });

      const data = await response.json();
      const lastMsgs = data?.data?.last_msgs || [];

      // Convert API response to { question, answer } pairs
      const mappedHistory = [];
      let currentQuestion = null;

      lastMsgs.forEach((item) => {
        if (item.role === "user") {
          currentQuestion = {
            question: item.content,
            answer: null,
            sources: [],
            suggestions: [],
            id: item.id,
            reason_type: item.reason_type || null,
            reason_text: item.reason_text || null
          };
          mappedHistory.push(currentQuestion);
        }
        else if (item.role === "ai" && currentQuestion) {
          currentQuestion.answer = item.content;
          currentQuestion.id = item.id;
          currentQuestion.reason_type = item.reason_type || null;
          currentQuestion.reason_text = item.reason_text || null;

          // include sources if provided
          if (item.sources) currentQuestion.sources = item.sources;

          // include suggestions if provided
          if (item.suggestions) currentQuestion.suggestions = item.suggestions;
        }
      });

      // Clear old chat and set new one
      setChatHistory(mappedHistory);

      // Redirect to NewChat so it renders
      navigate("/new-chat", {
        state: { restoredChat: mappedHistory },
      });

      setLoading(false);

    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  return (
    <div className={`sidemenu ${isSidebarCollapsed ? "collapsed" : ""}`}>
      <div className="sidemenu-top">
        <div className="logo">
          <a
            href="#"
            id="backBtn"
            title="Back"
            className="back-btn"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
            }}
          >
            <div className="icon-circle">
              <i className="fa fa-chevron-left" aria-hidden="true"></i>
            </div>
          </a>
          <Link to="/">
            <img src={logo} /> EVE
          </Link>
        </div>

        {/* Navigation Links */}
        <div className="sidebar-menu-item">
          <a href="/new-chat" onClick={handleNewChatClick} className="new-chat">
            <i className="fa-solid fa-pen-to-square"></i> New Chat
          </a>
        </div>
        <div className="sidebar-menu-item">
          <Link to="/docs" onClick={handlerouteClick} className="docs-link">
            <i className="fa-solid fa-book"></i> Strategies, policies and prescript documents
          </Link>
        </div>
        <div className="sidebar-menu-item">
          <Link to="/faq" onClick={handlerouteClick} className="faqs-link">
            <i className="fa-solid fa-circle-question"></i> FAQs
          </Link>
        </div>

        {/* Chat History Section */}
        <div className="old-chat">
          <h2>History</h2>
          <ul id="oldChatList">
            {apiHistory.length === 0 ? (
              <li>{loading ? "Loading..." : "No history available"}</li>
            ) : (
              apiHistory.map((item, i) => (
                <li
                  key={i}
                  className="thread-li"
                  style={{
                    backgroundColor: selectedThread === item.id ? "#ddddddff" : "transparent",
                  }}
                  onClick={() => handleHistoryClick(item.id)}
                >
                  {/* Clicking title loads history */}
                  <div>
                    {item.title.length > 40 
                      ? item.title.slice(0, 40) + "..." 
                      : item.title}
                  </div>

                  {/* Three Dots */}
                  <div
                    className="three-dots"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenu(openMenu === item.id ? null : item.id);
                    }}
                  >
                    <i className="fa fa-ellipsis-v"></i>
                  </div>

                  {/* Dropdown menu */}
                  {openMenu === item.id && (
                    <div
                      className="dropdown-menu sidebar-dm"
                      ref={menuRef}
                    >
                      <div className="dropdown-item sidebar-di" onClick={() => handleReload(item.id)}>
                        <i className="fa fa-history sidebar-icon"></i>
                        Reload
                      </div>
                      <div className="dropdown-item sidebar-di" onClick={() => openEditPopup(item)}>
                        <i className="fa fa-pencil sidebar-icon"></i>
                        Edit
                      </div>
                      <div className="dropdown-item sidebar-di" onClick={() => handleDelete(item.id)}>
                        <i className="fa fa-trash sidebar-icon"></i>
                        Delete
                      </div>
                    </div>
                  )}
                </li>

              ))
            )}
          </ul>

          {/* Load More Button */}
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="load-more"
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          )}
        </div>
      </div>

      {editPopup.open && (
        <div
          className="rename-model"
        >
          <div
            className="rename-content"
          >
            <h3 className="rename-title">Edit Title</h3>
            <textarea
              value={editPopup.thread.title}
              onChange={(e) =>
                setEditPopup((prev) => ({
                  ...prev,
                  thread: { ...prev.thread, title: e.target.value }
                }))
              }
              className="rename-input"
              rows={3}
              style={{ resize: "vertical"}}
            />

            <div className="rename-actions">
              <button onClick={handleRename} className="update-rename">
                Update
              </button>

              <button
                onClick={() => setEditPopup({ open: false, thread: null })}
                className="cancel-rename"
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {loading && (
        <div className="global-loader">
          <div className="loader-circle"></div>
          <div className="loader-text">Loading...</div>
        </div>
      )}

    </div>
  );
});

export default Sidebar;